/**
 * Iter104 — Claire in-app AI assistant.
 *
 * - Floating FAB on the BOTTOM-LEFT (WhatsAppWidget already occupies
 *   bottom-right). Visible only when the user is authenticated.
 * - On open: greeting + 3 contextual quick replies derived from the
 *   current route (`window.location.pathname`).
 * - Posts to POST /api/claire/chat. Sends `module_path` so the backend
 *   can stamp Claire's system prompt with where the user is.
 * - Persists session_id + last 12 turns in localStorage so the chat
 *   survives page navigation (it remounts on every route change).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Send, X, Loader2, RotateCcw, Phone, ArrowRight, Zap, Brain, Mic, MicOff } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "alp.claire_session_id";
const HISTORY_KEY = "alp.claire_history";
const ENGINE_KEY = "alp.claire_engine";
const MAX_HISTORY = 12;

// Iter276 · Detect the user's current UI language so Claire replies in
// the same one. Falls back to browser language when the switcher hasn't
// been touched, else "en" as the final default.
function detectLang() {
  try {
    const saved = window.localStorage.getItem("alp.lang");
    if (["pt", "en", "es"].includes(saved)) return saved;
  } catch (_e) { /* ignore */ }
  const nav = (typeof navigator !== "undefined" && navigator.language || "").toLowerCase();
  if (nav.startsWith("pt")) return "pt";
  if (nav.startsWith("es")) return "es";
  return "en";
}

function loadEngine() {
  try {
    const v = window.localStorage.getItem(ENGINE_KEY);
    if (v === "sonnet5" || v === "perplexity") return v;
  } catch (_e) { /* ignore */ }
  return "sonnet5";
}

// Per-route quick replies. Falls back to a generic set when the path
// isn't in the map. Keep these short — they double as the prompts users
// will read first.
const ROUTE_HINTS = {
  "/dashboard": {
    label: "Dashboard",
    suggestions: [
      "Como interpreto meus KPIs?",
      "Como adiciono uma nova fonte de leads?",
      "O que é Lead Score?",
    ],
  },
  "/config": {
    label: "Auto Pilot",
    suggestions: [
      "Como configuro filtros de público-alvo?",
      "Quantas cidades posso adicionar?",
      "O que o Auto Pilot faz por mim?",
    ],
  },
  "/prospecting": {
    label: "Lead Discovery",
    suggestions: [
      "Qual a melhor keyword para meu nicho?",
      "Como filtrar por porte da empresa?",
      "Como exportar os resultados?",
    ],
  },
  "/kanban": {
    label: "CRM Kanban",
    suggestions: [
      "Como movo leads entre estágios?",
      "Como crio uma cadência de outreach daqui?",
      "Como marco um lead como Won?",
    ],
  },
  "/gov-kanban": {
    label: "Gov Kanban",
    suggestions: [
      "Como acompanho licitações públicas?",
      "Como exportar oportunidades públicas?",
      "Como filtrar por agência?",
    ],
  },
  "/public-works": {
    label: "Procurement Scanner",
    suggestions: [
      "Como funciona o Procurement Scanner?",
      "Qual a frequência de atualização?",
      "Como salvar um edital favorito?",
    ],
  },
  "/sunbiz": {
    label: "Sunbiz Connector",
    suggestions: [
      "Como funciona o pipeline diário do Sunbiz?",
      "Como mudar o filtro de cidades-alvo?",
      "Posso re-rodar o pipeline manualmente?",
    ],
  },
  "/b2c-intent": {
    label: "B2C Intent",
    suggestions: [
      "Que sinais o B2C Intent captura?",
      "Como criar uma busca por intenção?",
      "Quais redes sociais são monitoradas?",
    ],
  },
  "/b2c-radar": {
    label: "B2C Lead Radar",
    suggestions: [
      "Como funciona o Lead Radar?",
      "Como exportar leads B2C?",
      "Quais filtros recomendam?",
    ],
  },
  "/lead-gap": {
    label: "Lead Gap Intelligence",
    suggestions: [
      "Como funciona o Lead Gap?",
      "Como descubro leads que o concorrente atende?",
      "Esse módulo está no meu plano?",
    ],
  },
  "/campaigns": {
    label: "Campanhas",
    suggestions: [
      "Como crio uma cadência multi-canal?",
      "Como uso variáveis dinâmicas no email?",
      "Como agendar disparos?",
    ],
  },
  "/insights": {
    label: "Insights",
    suggestions: [
      "Quais métricas o painel mostra?",
      "Como exportar relatório?",
      "Como medir ROI por canal?",
    ],
  },
  "/roi": {
    label: "ROI Dashboard",
    suggestions: [
      "Como o ROI é calculado?",
      "Posso ver ROI por cadência?",
      "Como melhorar minha conversão?",
    ],
  },
  "/replies": {
    label: "Smart Replies",
    suggestions: [
      "Como a IA classifica respostas?",
      "Posso editar a sugestão antes de enviar?",
      "Como conectar minha caixa de entrada?",
    ],
  },
  "/integrations": {
    label: "Integrações",
    suggestions: [
      "Como conecto Apify ao sistema?",
      "Como ativar o Hunter.io?",
      "Onde colo minha API key Resend?",
    ],
  },
  "/webhooks": {
    label: "Webhooks",
    suggestions: [
      "Quais eventos posso receber?",
      "Como testo um webhook?",
      "Como reenviar um evento que falhou?",
    ],
  },
  "/library": {
    label: "Biblioteca",
    suggestions: [
      "Como uso os templates da biblioteca?",
      "Posso criar meu próprio template?",
      "Como compartilhar uma sequência?",
    ],
  },
  "/instagram": {
    label: "Instagram Scraper",
    suggestions: [
      "Como configurar um job do Instagram?",
      "Qual o limite diário do scraper?",
      "Como extrair contato do bio?",
    ],
  },
  "/linkedin": {
    label: "LinkedIn Sales Nav",
    suggestions: [
      "Como configuro meu cookie li_at?",
      "Qual o limite seguro de scraping?",
      "Como filtrar por cargo decisor?",
    ],
  },
  "/youtube": {
    label: "YouTube Scraper",
    suggestions: [
      "Como funciona o scraper do YouTube?",
      "Como pegar contato do canal?",
      "Como filtrar por nicho?",
    ],
  },
  "/marketplace": {
    label: "App Marketplace",
    suggestions: [
      "Que apps posso instalar?",
      "Como conectar um app de terceiros?",
      "Quanto custa cada app?",
    ],
  },
  "/billing": {
    label: "Billing",
    suggestions: [
      "Como faço upgrade do meu plano?",
      "Como cancelar assinatura?",
      "Onde baixo minhas faturas?",
    ],
  },
  "/marketing-apis": {
    label: "Marketing APIs",
    suggestions: [
      "Como conectar Google Analytics?",
      "Como linkar Meta Ads?",
      "Posso ver dados consolidados?",
    ],
  },
  "/master": {
    label: "Master Console",
    suggestions: [
      "Como crio um novo tenant?",
      "Como ativar uma conta de demo?",
      "Onde vejo o uso por cliente?",
    ],
  },
};

const GENERIC_SUGGESTIONS = [
  "O que é o AgenteLead Pro?",
  "Como começo a captar leads agora?",
  "Quais módulos meu plano libera?",
];

// Parse `[label](/route)` markdown links inside Claire's plain-text reply.
// Internal routes (start with `/` and NOT `//`) get in-app navigation via
// React Router; anything else (mailto:, tel:, http://, wa.me, etc.) opens
// in a new tab. Bold `**text**` becomes <strong>.
//
// Iter283 · New extensions understood by renderClaireContent:
//   • `[chip:Label](/route)` — full-width prominent action chip
//   • `[[CONFIG_ERROR:engine]]` — friendly config card with a shortcut to
//     `/integrations` instead of the raw 503 message.
//   • `| col1 | col2 |` markdown tables — rendered as compact 2-col cards
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const CONFIG_ERR_RE = /\[\[CONFIG_ERROR:([a-z0-9_]+)\]\]/gi;
const TABLE_LINE_RE = /^\s*\|.+\|\s*$/;

function renderClaireContent(text, onInternalNav, t) {
  if (!text) return null;

  // Iter283 · CONFIG_ERROR tokens replace raw 503 stack traces with a
  // friendly card that has a direct "Open Integrations" button.
  const configMatch = CONFIG_ERR_RE.exec(text);
  CONFIG_ERR_RE.lastIndex = 0;
  if (configMatch) {
    const engine = configMatch[1];
    return (
      <div data-testid="claire-config-error-card"
           className="rounded-lg border p-3 my-1"
           style={{ background: "rgba(239,68,68,0.10)", borderColor: "rgba(239,68,68,0.35)" }}>
        <div className="text-[11px] uppercase font-black tracking-widest text-red-300 mb-1.5">
          {t ? t("claire.config_required") : "Configuration required"}
        </div>
        <div className="text-[13px] text-white/90 leading-snug mb-3">
          {engine === "perplexity"
            ? "Preciso da chave da Perplexity para responder pelo motor Fast. Adicione uma vez e tudo passa a funcionar."
            : "Este agente ainda não está conectado. Peça ao admin para plugar a chave em Integrações."}
        </div>
        <button type="button"
          data-testid="claire-config-error-cta"
          onClick={() => onInternalNav && onInternalNav("/integrations")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-bold"
          style={{ background: "#84CC16", color: "#0F172A" }}>
          {t ? t("claire.open_integrations") : "Open Integrations"} <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Iter283 · Parse markdown tables (rows with `| ... |` on separate
  // lines). Render as a compact card list. Everything else falls
  // through to the existing link/bold parsing.
  const lines = text.split(/\r?\n/);
  const hasTable = lines.some((l) => TABLE_LINE_RE.test(l));
  if (hasTable) {
    const chunks = [];
    let buf = [];
    let inTable = false;
    const flushTable = () => {
      if (buf.length === 0) return;
      // Ignore separator rows `|---|---|` if present.
      const rows = buf.filter((l) => !/^\s*\|[-\s|]+\|\s*$/.test(l))
        .map((l) => l.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim()));
      chunks.push({ kind: "table", rows });
      buf = [];
    };
    for (const l of lines) {
      if (TABLE_LINE_RE.test(l)) {
        inTable = true; buf.push(l);
      } else {
        if (inTable) { flushTable(); inTable = false; }
        if (l.trim()) chunks.push({ kind: "text", value: l });
      }
    }
    if (inTable) flushTable();
    return chunks.map((ch, i) => {
      if (ch.kind === "table") {
        const header = ch.rows[0] || [];
        const body = ch.rows.slice(1);
        return (
          <div key={`tbl-${i}`} className="my-1.5 rounded-lg overflow-hidden border border-white/10"
               data-testid="claire-mini-table">
            <div className="grid grid-cols-2 gap-0 text-[11px] font-black uppercase tracking-widest bg-[#7C3AED]/25 text-white px-2 py-1.5">
              <span>{header[0] || ""}</span>
              <span className="text-right">{header[1] || ""}</span>
            </div>
            {body.map((row, ri) => (
              <div key={ri} className="grid grid-cols-2 gap-2 text-[12px] px-2 py-1.5 border-t border-white/5">
                <span className="text-white/80">{row[0] || ""}</span>
                <span className="text-white text-right font-bold tabular-nums">{row[1] || ""}</span>
              </div>
            ))}
          </div>
        );
      }
      return <div key={`ln-${i}`}>{renderInline(ch.value, onInternalNav)}</div>;
    });
  }

  return renderInline(text, onInternalNav);
}

// Iter283 · Extracted inline renderer so table cells + plain messages
// share the same link/chip/bold parsing.
function renderInline(text, onInternalNav) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    }
    nodes.push({ kind: "link", label: match[1], href: match[2] });
    lastIndex = LINK_RE.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push({ kind: "text", value: text.slice(lastIndex) });
  }

  return nodes.map((n, i) => {
    if (n.kind === "link") {
      const isInternal = n.href.startsWith("/") && !n.href.startsWith("//");
      const isAnchor = n.href.startsWith("#");
      // Iter283 · `chip:` prefix upgrades a link into a big action button.
      const isChip = n.label.startsWith("chip:");
      const label = isChip ? n.label.slice(5) : n.label;
      const commonProps = {
        key: `lnk-${i}`,
        href: n.href,
        onClick: (e) => {
          if (isInternal && !isAnchor && onInternalNav) {
            e.preventDefault();
            onInternalNav(n.href);
          }
        },
        target: isInternal ? undefined : "_blank",
        rel: isInternal ? undefined : "noopener noreferrer",
        "data-testid": isChip ? `claire-chip-${n.href}` : `claire-link-${n.href}`,
      };
      if (isChip) {
        return (
          <a {...commonProps}
             className="inline-flex items-center gap-1.5 my-1 mr-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-bold transition-colors"
             style={{ background: "#84CC16", color: "#0F172A" }}>
            {label} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        );
      }
      return (
        <a {...commonProps}
           className="inline-flex items-center gap-1 my-0.5 px-2 py-0.5 rounded-md bg-[#C9A24E]/20 hover:bg-[#C9A24E]/35 text-[#F5D889] hover:text-white text-[12.5px] font-semibold underline-offset-2 hover:underline transition-colors">
          {label}
          {isInternal && <ArrowRight className="w-3 h-3" />}
        </a>
      );
    }
    // Split text on bold markers.
    const parts = n.value.split(BOLD_RE);
    return parts.map((p, j) =>
      j % 2 === 1
        ? <strong key={`b-${i}-${j}`} className="font-semibold text-white">{p}</strong>
        : <span key={`t-${i}-${j}`}>{p}</span>,
    );
  });
}

function loadSession() {
  try {
    let s = window.localStorage.getItem(SESSION_KEY);
    if (!s) {
      s =
        window.crypto?.randomUUID?.() ||
        `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch (_e) {
    return `s_${Date.now()}`;
  }
}

// Iter328 · ClaireActionChip — renders the [[ACTION:tool:args]] tokens
const ACTION_LABELS = {
  open_tutorial:  "Assistir tutorial",
  open_dossier:   "Abrir dossiê",
  save_prospect:  "Salvar prospect",
  create_task:    "Criar tarefa",
  email_status:   "Ver status do e-mail",
};

function ClaireActionChip({ action, onNavigate, testid }) {
  const [state, setState] = useState("idle"); // idle|running|ok|fail
  const [detail, setDetail] = useState("");

  const run = async () => {
    setState("running");
    try {
      let body;
      const args = action.args || [];
      if (action.tool === "open_tutorial") {
        body = { tutorial_id: args[0] };
      } else if (action.tool === "open_dossier") {
        body = { company_id: args[0] };
      } else if (action.tool === "save_prospect") {
        body = { name: args[0], company: args[1],
                 email: args[2] || null };
      } else if (action.tool === "create_task") {
        body = { title: args[0], due_at: args[1] || null };
      } else if (action.tool === "email_status") {
        // Accept either prospect_id or email
        const first = args[0] || "";
        body = first.includes("@")
          ? { email: first }
          : { prospect_id: first };
      } else {
        setState("fail");
        setDetail("Ferramenta desconhecida");
        return;
      }
      const { data } = await axios.post(
        `${API}/claire/actions/${action.tool}`, body);
      setDetail(data?.detail || "");
      if (data?.status === "completed") {
        setState("ok");
        const route = data?.data?.route;
        if (route && onNavigate) {
          setTimeout(() => onNavigate(route), 350);
        }
      } else if (data?.status === "partial") {
        setState("ok");
      } else {
        setState("fail");
      }
    } catch (e) {
      setState("fail");
      setDetail(e?.response?.data?.detail || "Falhou");
    }
  };

  const label = ACTION_LABELS[action.tool] || action.tool;
  const args = (action.args || []).slice(0, 2).join(" · ");
  const color = state === "ok"
    ? "bg-emerald-500/15 border-emerald-400/60 text-emerald-200"
    : state === "fail"
      ? "bg-red-500/15 border-red-400/60 text-red-200"
      : state === "running"
        ? "bg-white/10 border-white/30 text-white/80"
        : "bg-[#C9A24E]/20 border-[#C9A24E]/40 text-[#F5D889] hover:bg-[#C9A24E]/35";
  return (
    <button
      type="button"
      onClick={run}
      disabled={state === "running" || state === "ok"}
      data-testid={testid}
      title={detail || `${label} — ${args}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-semibold border transition ${color}`}>
      {state === "running" && <Loader2 className="w-3 h-3 animate-spin" />}
      {state === "ok" && "✓ "}
      {state === "fail" && "✕ "}
      {label}
      {args ? <span className="opacity-70 font-normal">· {args}</span> : null}
    </button>
  );
}

function loadHistory() {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_HISTORY) : [];
  } catch (_e) {
    return [];
  }
}

function saveHistory(history) {
  try {
    window.localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(history.slice(-MAX_HISTORY)),
    );
  } catch (_e) {
    /* ignore quota errors */
  }
}

function matchRouteHint(pathname) {
  if (!pathname) return null;
  if (ROUTE_HINTS[pathname]) return ROUTE_HINTS[pathname];
  // Longest-prefix match (e.g. /master/tenants → /master).
  const keys = Object.keys(ROUTE_HINTS).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (pathname.startsWith(k)) return ROUTE_HINTS[k];
  }
  return null;
}

export default function ClaireWidget({ mode = "app" }) {
  const { user } = useAuth();
  const { t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const goInternal = (href) => {
    setOpen(false);
    navigate(href);
  };
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => loadSession());
  const [messages, setMessages] = useState(() => loadHistory());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [lastUncertain, setLastUncertain] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  // Iter276 · engine toggle (Fast=perplexity, Deep=sonnet5) — persisted
  // per-user via localStorage. Defaults to Sonnet 5 because it's always
  // available via Emergent LLM Key.
  const [engine, setEngine] = useState(() => loadEngine());
  useEffect(() => {
    try { window.localStorage.setItem(ENGINE_KEY, engine); } catch (_e) { /* ignore */ }
  }, [engine]);
  const scrollRef = useRef(null);

  const hint = useMemo(() => matchRouteHint(pathname), [pathname]);
  const isSales = mode === "sales";
  const suggestions = isSales
    ? [
        "Quanto custa o plano Premium?",
        "Como funciona o FREE TRIAL?",
        "Quero ver uma demonstração",
      ]
    : (hint?.suggestions || GENERIC_SUGGESTIONS);
  const greeting = isSales
    ? "Olá! 👋 Sou a Claire, do AgenteLead Pro. Posso te explicar planos, agendar uma demo ou tirar dúvidas. O que você precisa?"
    : (hint
        ? `Olá! 👋 Sou a Claire. Vi que você está em **${hint.label}** — posso te ajudar com algo aqui?`
        : "Olá! 👋 Sou a Claire, sua assistente do AgenteLead Pro. O que posso explicar agora?");

  // Fetch the widget config (WhatsApp URL) once on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/claire/config`)
      .then(({ data }) => { if (!cancelled) setWhatsappUrl(data?.whatsapp_url || ""); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Persist messages whenever they change (cap last 12).
  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  // Auto-scroll to bottom on new message / typing indicator.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  // Seed a single greeting the first time the user opens the panel.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: greeting }]);
      // Iter301 · Proactive BOI Compliance suggestion — only on the
      // in-app widget (not the sales landing widget) and only once
      // per session. If the master has any overdue FinCEN filing on
      // record, Claire surfaces the top one with a suggested angle:
      // "compliance renewal + monitoring package".
      if (!isSales && !sessionStorage.getItem("claire.boi.suggested")) {
        sessionStorage.setItem("claire.boi.suggested", "1");
        axios.get(`${API}/smith/sunbiz/boi/dashboard?window=overdue&limit=1`)
          .then(({ data }) => {
            const top = (data?.rows || [])[0];
            if (!top) return;
            const late = top.days_left < 0 ? `${-top.days_left}d de atraso` : "vencendo";
            const name = top.company_name || "esse prospect";
            const pid  = top.prospect_id;
            const msg =
              `💡 **Alerta de conformidade FinCEN detectado**\n\n` +
              `**${name}** está com o BOI **${late}** — prazo era \`${top.deadline || "—"}\`.\n\n` +
              `Ângulo de outreach recomendado: **"pacote de renovação de conformidade + monitoramento contínuo"**. ` +
              `Empresas com BOI vencido correm risco de multas FinCEN (US$591/dia) e costumam pagar prêmio por ` +
              `pacotes preventivos que combinam compliance + segurança física ALPR.\n\n` +
              (pid
                ? `[chip:Abrir ${name}](/smith/prospects/${pid}) · [Ver todos vencidos](/smith/prospects?boi=overdue)`
                : `[chip:Ver todos vencidos](/smith/prospects?boi=overdue)`);
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: msg,
                meta: { source: "boi_suggestion", prospect_id: pid }},
            ]);
          })
          .catch(() => { /* silent — non-fatal */ });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messages.length, greeting]);

  // Listen for `claire:open` CustomEvents fired by <ClaireHelpButton />.
  // Opens the panel, pre-fills the input with `prefill`, and optionally
  // auto-sends so the user gets an instant answer from the context they
  // were in. Tolerant of missing/odd payloads.
  useEffect(() => {
    const onOpen = (e) => {
      const detail = (e && e.detail) || {};
      const prefill = (detail.prefill || "").toString();
      const seedMessage = (detail.seed_message || "").toString();  // Iter306
      const autoSend = !!detail.autoSend;
      setOpen(true);
      if (seedMessage) {
        // Iter306 · Inject the seed message as a user turn immediately
        // so Claire's next reply is contextual to the caller (Buying
        // Signals card, prospect drawer, etc). Defer to let the panel
        // + greeting mount first.
        setTimeout(() => send(seedMessage), 80);
        return;
      }
      if (prefill) {
        if (autoSend) {
          setTimeout(() => send(prefill), 60);
        } else {
          setInput(prefill);
        }
      }
    };
    window.addEventListener("claire:open", onOpen);
    return () => window.removeEventListener("claire:open", onOpen);
    // `send` is defined below in the same component and closes over the
    // latest state via setMessages(prev=>...). Re-binding on every render
    // keeps the listener pointing at the freshest closure.
  });

  // ─── Iter279 · Voice input hooks — must live BEFORE the early
  // returns below to comply with the Rules of Hooks. ────────────────
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  // Iter280 · Voice Transcript Correction — when we've just written a
  // Whisper transcript into the input, remember what it was so the UI
  // can show a subtle "correct before sending" banner and give the
  // user a chance to fix mishearings.
  const [transcriptDraft, setTranscriptDraft] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Iter280 · Waveform — mounted analyser + a rolling amplitude buffer
  // so the mic button can pulse in sync with what the mic is hearing.
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [waveLevel, setWaveLevel] = useState(0);
  const supportsMic = typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window !== "undefined"
    && typeof window.MediaRecorder !== "undefined";

  // Don't render the "app" instance for anonymous visitors — they should
  // see the "sales" instance (mounted separately on public routes).
  if (!isSales && !user) return null;
  // Inversely, don't render the "sales" instance for logged-in users on
  // those public routes; the authenticated <ClaireWidget /> takes over.
  if (isSales && user) return null;

  // Iter120 — Inside the Master Console the header hosts a dedicated
  // "Claire" button (see MasterLayout). Suppress the floating FAB there
  // so it no longer overlaps the sidebar & data tables. The panel still
  // opens via the `claire:open` CustomEvent, so users get the widget in
  // a header-anchored position without visual clutter.
  // Iter129 — the floating FAB was retired app-wide for logged-in users
  // (it overlapped the sidebar). Every page header now hosts a
  // <ClaireHelpButton /> that opens the widget via the `claire:open` event.
  // The FAB remains only for the public "sales" instance.
  const hideFab = !isSales;

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    const next = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      // Build a Perplexity-safe history payload:
      //   • Drop the local greeting bubble (assistant seeded by us).
      //   • Drop the just-appended user turn (it travels in `message`).
      //   • Drop assistant turns whose content equals an error fallback —
      //     they were never round-tripped through Perplexity and would
      //     just pollute alternation.
      const historyPayload = messages
        .filter((m, i) => !(i === 0 && m.role === "assistant"))
        .map((m) => ({ role: m.role, content: m.content }));
      // Iter326 · Structured interface context so Claire never guesses.
      // We collect the visible filters/counts from a global window store
      // that pages can populate via `window.__claireContext = {...}`.
      // The backend re-validates tenant scope regardless of this payload.
      let uiContext = null;
      try {
        if (typeof window !== "undefined" && window.__claireContext) {
          uiContext = window.__claireContext || null;
        }
      } catch (_e) { uiContext = null; }
      const { data } = await axios.post(`${API}/claire/chat`, {
        message: trimmed,
        history: historyPayload.slice(-MAX_HISTORY),
        module_path: pathname,
        session_id: sessionId,
        mode,
        engine,
        lang: detectLang(),
        context: uiContext,
      });
      // Iter328 · Parse [[ACTION:tool:args]] tokens into structured
      // suggestions. Actions stay as chips the user must click to
      // execute — nothing runs automatically.
      const raw = data?.reply || "...";
      const actions = [];
      const cleaned = raw.replace(
        /\[\[ACTION:([a-z_]+):([^\]]+)\]\]/gi,
        (_m, tool, args) => {
          actions.push({ tool, args: String(args).split("|") });
          return "";
        },
      ).trim();
      setMessages((prev) => [
        ...prev,
        { role: "assistant",
          content: cleaned || raw,
          sources: Array.isArray(data?.sources) ? data.sources : [],
          audit: data?.evidence_audit || null,
          actions },
      ]);
      setLastUncertain(!!data?.uncertain);
    } catch (err) {
      // Surface a meaningful reason so users understand what happened
      // instead of the generic "Tive um problema agora".
      const backendDetail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let friendly = "Tive um problema agora 😕 — tente reenviar em alguns segundos.";
      if (typeof backendDetail === "string" && backendDetail.length < 300) {
        friendly = backendDetail;
      } else if (status === 429) {
        friendly = "Muitas perguntas seguidas — respira 30 segundos e manda de novo 🙂";
      } else if (status === 503) {
        // Iter283 · render a friendly config-error card instead of a
        // raw stack trace. The token is picked up by renderClaireContent.
        const detectEngine = /perplexity/i.test(String(backendDetail || "")) ? "perplexity" : "sonnet5";
        friendly = `[[CONFIG_ERROR:${detectEngine}]]`;
      } else if (status === 502 || status === 504) {
        friendly = "A conexão com o modelo demorou demais. Tenta de novo em 10 segundos — se persistir, [me chame no WhatsApp](/#whatsapp).";
      }
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: friendly },
      ]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setMessages([{ role: "assistant", content: greeting }]);
    try {
      window.localStorage.removeItem(HISTORY_KEY);
    } catch (_e) {
      /* ignore */
    }
  };

  // ─── Iter279 · Voice helpers (state hooks declared above) ─────────
  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch (_e) { /* ignore */ }
    }
    // Iter280 · Waveform cleanup
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (_e) { /* ignore */ }
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setWaveLevel(0);
  };

  const startRecording = async () => {
    if (!supportsMic) {
      setMessages((prev) => [...prev, { role: "assistant",
        content: "Seu navegador não suporta gravação de áudio. Digite sua pergunta 🎙️" }]);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer opus in webm — smallest, universally supported.
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      mr.onstop = async () => {
        setRecording(false);
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (audioContextRef.current) {
          try { audioContextRef.current.close(); } catch (_e) { /* ignore */ }
          audioContextRef.current = null;
        }
        analyserRef.current = null;
        setWaveLevel(0);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1500) {
          setMessages((prev) => [...prev, { role: "assistant",
            content: "Gravação muito curta 🎙️ · segure o mic e fale a pergunta." }]);
          return;
        }
        await transcribeAndSend(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);

      // Iter280 · Wire up Web Audio analyser for the real-time waveform.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ac = new AC();
          audioContextRef.current = ac;
          const source = ac.createMediaStreamSource(stream);
          const analyser = ac.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.6;
          source.connect(analyser);
          analyserRef.current = analyser;
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            const a = analyserRef.current;
            if (!a) return;
            a.getByteTimeDomainData(buf);
            // RMS-ish amplitude 0..1
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
              const v = (buf[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.min(1, Math.sqrt(sum / buf.length) * 2.4);
            setWaveLevel(rms);
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        }
      } catch (_e) { /* analyser is best-effort */ }

      // Auto-stop after 30s to avoid runaway captures.
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 30000);
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Permissão de microfone negada — habilite nas configurações do navegador."
        : `Não foi possível iniciar o microfone: ${err?.message || err}`;
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
    }
  };

  const transcribeAndSend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append("lang", detectLang());
      const { data } = await axios.post(`${API}/claire/voice/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = (data?.transcript || "").trim();
      if (!transcript) {
        setMessages((prev) => [...prev, { role: "assistant",
          content: "Não entendi o áudio. Tente falar mais próximo do microfone." }]);
        return;
      }
      // Iter280 · Voice Transcript Correction — DON'T auto-send.
      // Drop the transcript into the input, focus + select it so the
      // user can quickly correct any Whisper mishearing ("Blake" → "black")
      // before firing to Claire. If it looks right they just tap send.
      setInput(transcript);
      setTranscriptDraft(transcript);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="claire-widget-input"]');
        if (el && typeof el.focus === "function") {
          el.focus();
          try { el.setSelectionRange(0, transcript.length); } catch (_e) { /* ignore */ }
        }
      }, 40);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const friendly = typeof detail === "string" ? detail
        : status === 503 ? "Voice ainda não está configurado neste servidor."
        : status === 413 ? "Áudio muito longo — grave até 30 segundos."
        : "Falha ao transcrever o áudio. Tente novamente.";
      setMessages((prev) => [...prev, { role: "assistant", content: friendly }]);
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <>
      {/* Floating button — bottom-LEFT (WhatsApp owns bottom-right;
          top-right is reserved for the user/role badge in al-shell).
          Iter120 — suppressed inside /master/* where the header hosts
          a dedicated "Claire" button. The panel still opens via the
          `claire:open` CustomEvent. */}
      {!hideFab && (
      <button
        type="button"
        data-testid="claire-widget-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fechar Claire" : "Abrir Claire"}
        className={
          "fixed z-[60] h-11 px-3.5 rounded-full shadow-lg shadow-[#7C3AED]/30 " +
          "ring-1 ring-white/10 hover:scale-[1.03] transition-transform " +
          "flex items-center gap-2 bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white " +
          // Iter303 · Auth pages (login/signup/forgot) have a testimonial
          // stack anchored to bottom-left. Shift Claire to bottom-right
          // there so the fab doesn't cover reviews / social proof.
          (
            /^\/(login|signup|forgot|reset)/.test(pathname)
              ? "bottom-6 right-6"
              : "bottom-6 left-6"
          )
        }
        title={open ? "Fechar Claire" : "Falar com a Claire"}
      >
        {open ? (
          <>
            <X className="w-4 h-4" data-testid="claire-widget-icon-close" />
            <span className="text-[12px] font-semibold hidden sm:inline">Fechar</span>
          </>
        ) : (
          <>
            <span className="relative inline-flex">
              <Sparkles className="w-4 h-4" data-testid="claire-widget-icon-open" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-[#4F46E5] animate-pulse" />
            </span>
            <span className="text-[12px] font-bold tracking-tight hidden sm:inline">
              {isSales ? "Falar com a Claire" : "Claire"}
            </span>
          </>
        )}
      </button>
      )}

      {open && (
        <div
          data-testid="claire-widget-panel"
          className={`fixed z-[60] w-[min(96vw,380px)] h-[540px] max-h-[calc(100vh-7rem)] rounded-2xl overflow-hidden shadow-2xl border border-white/10 flex flex-col bg-[#0F1B40] ${
            hideFab
              ? "top-16 right-4 sm:right-6"
              // Iter303 · Match the fab side on auth pages so the panel
              // opens above/near the fab instead of on the wrong corner.
              : (/^\/(login|signup|forgot|reset)/.test(pathname)
                  ? "bottom-24 right-4 sm:right-6"
                  : "bottom-24 left-4 sm:left-6")
          }`}
          style={{ fontFamily: "'Plus Jakarta Sans','Inter',system-ui,sans-serif" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-[#C9A24E]/60">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[14px] font-bold leading-tight"
                data-testid="claire-widget-title"
              >
                {isSales ? "Claire · Comercial AgenteLead" : "Claire · Assistente AgenteLead"}
              </div>
              <div className="text-[10.5px] text-white/75">
                {isSales
                  ? "Planos, demos e dúvidas comerciais"
                  : (hint ? `Contexto: ${hint.label}` : "Pergunte sobre qualquer módulo")}
              </div>
            </div>
            <button
              type="button"
              onClick={resetChat}
              data-testid="claire-widget-reset"
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label={t("claire.reset")}
              title={t("claire.reset")}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="claire-widget-close"
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
              aria-label={t("claire.close")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Iter276 · Engine toggle (Deep=Sonnet 5, Fast=Perplexity Sonar Pro) */}
          {!isSales && (
            <div className="bg-[#0A1233] border-b border-white/8 px-3 py-2 flex items-center gap-2"
                 data-testid="claire-widget-engine-bar">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/45">
                {t("claire.engine")}
              </span>
              <div className="flex items-center gap-1 p-0.5 rounded-full bg-white/[0.06] border border-white/10">
                <button type="button"
                  data-testid="claire-engine-sonnet5"
                  onClick={() => setEngine("sonnet5")}
                  aria-pressed={engine === "sonnet5"}
                  title={t("claire.engine.deep_title")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                    engine === "sonnet5"
                      ? "bg-[#7C3AED] text-white shadow"
                      : "text-white/60 hover:text-white"
                  }`}>
                  <Brain className="w-3 h-3" /> {t("claire.engine.deep")}
                </button>
                <button type="button"
                  data-testid="claire-engine-perplexity"
                  onClick={() => setEngine("perplexity")}
                  aria-pressed={engine === "perplexity"}
                  title={t("claire.engine.fast_title")}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider transition-colors ${
                    engine === "perplexity"
                      ? "bg-[#10B981] text-white shadow"
                      : "text-white/60 hover:text-white"
                  }`}>
                  <Zap className="w-3 h-3" /> {t("claire.engine.fast")}
                </button>
              </div>
              <span className="ml-auto text-[10px] text-white/40 hidden sm:inline">
                {engine === "sonnet5" ? "Sonnet 5" : "Sonar Pro"}
              </span>
            </div>
          )}

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="claire-widget-messages"
            className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
          >
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}-${(m.content || "").slice(0, 16)}`}
                data-testid={`claire-widget-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-snug whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-[#C9A24E] text-[#0A1233] rounded-br-sm"
                      : "bg-white/10 text-white rounded-bl-sm"
                  }`}
                >
                  {m.role === "assistant"
                    ? renderClaireContent(m.content, goInternal, t)
                    : m.content}
                  {/* Iter326 · Evidence audit + source chips */}
                  {m.role === "assistant" && m.audit?.verdict === "unverified_claim" && (
                    <div
                      className="mt-2 px-2 py-1 rounded-md border border-amber-400/40 bg-amber-500/10 text-[11px] text-amber-200 inline-flex items-center gap-1.5"
                      data-testid={`claire-audit-warning-${i}`}
                      title="A resposta traz afirmações factuais sem fonte referenciada — trate como não verificado.">
                      ⚠ Não verificado — sem fonte referenciada
                    </div>
                  )}
                  {m.role === "assistant" && Array.isArray(m.sources) && m.sources.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`claire-sources-${i}`}>
                      {m.sources.slice(0, 8).map((s) => (
                        <a
                          key={`${i}-src-${s.index}`}
                          href={s.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`claire-source-chip-${i}-${s.index}`}
                          title={s.title || s.url}
                          className={`text-[10.5px] px-1.5 py-0.5 rounded-md border transition ${
                            s.referenced_in_reply
                              ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                              : "border-white/20 bg-white/5 text-white/70 hover:bg-white/10"
                          }`}>
                          [{s.index}] {s.host}
                        </a>
                      ))}
                      {m.sources.length > 8 && (
                        <span className="text-[10.5px] text-white/50">
                          +{m.sources.length - 8}
                        </span>
                      )}
                    </div>
                  )}
                  {m.role === "assistant" && Array.isArray(m.actions) && m.actions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`claire-actions-${i}`}>
                      {m.actions.map((a, ai) => (
                        <ClaireActionChip
                          key={`${i}-a-${ai}`}
                          testid={`claire-action-${i}-${ai}`}
                          action={a}
                          onNavigate={goInternal}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div
                  className="bg-white/10 text-white/70 text-[12px] px-3 py-2 rounded-2xl inline-flex items-center gap-1.5"
                  data-testid="claire-widget-typing"
                >
                  <Loader2 className="w-3 h-3 animate-spin" /> {t("claire.typing")}
                </div>
              </div>
            )}
            {!sending && messages.length <= 1 && (
              <div className="pt-2 space-y-1.5" data-testid="claire-widget-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    data-testid={`claire-widget-suggestion-${s.slice(0, 16)}`}
                    className="block w-full text-left text-[12px] text-[#E5C572] hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-lg transition-colors"
                  >
                    → {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          {/* Iter280 · Transcript correction banner — shows after Whisper
              writes into the input. User can fix mishearings before send. */}
          {transcriptDraft && !recording && !transcribing && input === transcriptDraft && (
            <div data-testid="claire-widget-transcript-banner"
                 className="border-t border-b border-white/10 px-3 py-1.5 flex items-center gap-2 bg-[#7C3AED]/15">
              <Mic className="w-3.5 h-3.5 text-[#C9A24E]" />
              <span className="text-[11px] text-white/85 flex-1 leading-tight">
                {t("claire.voice.hint")}
              </span>
              <button type="button"
                onClick={() => { setTranscriptDraft(""); setInput(""); }}
                data-testid="claire-widget-transcript-clear"
                className="text-[10px] uppercase font-black tracking-widest px-2 py-0.5 rounded text-white/70 hover:text-white hover:bg-white/10">
                {t("claire.voice.clear")}
              </button>
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // clear the transcript draft flag once user submits
              setTranscriptDraft("");
              send();
            }}
            className="border-t border-white/10 p-2.5 flex items-center gap-2 bg-[#0A1233]"
            data-testid="claire-widget-composer"
          >
            <input
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // If user edits the transcript, drop the "just transcribed"
                // banner so it doesn't reappear on next tap of Mic.
                if (transcriptDraft && e.target.value !== transcriptDraft) {
                  setTranscriptDraft("");
                }
              }}
              placeholder={recording ? "Gravando…"
                : transcribing ? "Transcrevendo…"
                : "Pergunte à Claire…"}
              data-testid="claire-widget-input"
              disabled={recording || transcribing}
              className="flex-1 h-10 bg-white/5 border border-white/15 focus:border-[#7C3AED] outline-none rounded-full px-4 text-white text-[13px] placeholder-white/40 disabled:opacity-70"
            />
            {/* Iter280 · Waveform pulse — 5 bars scaled by RMS amplitude.
                Only visible while recording so the widget stays compact. */}
            {recording && (
              <div data-testid="claire-widget-waveform"
                   className="hidden sm:flex items-center gap-0.5 h-10 px-2 rounded-full bg-red-500/10 border border-red-500/40"
                   aria-hidden="true">
                {[0.4, 0.7, 1.0, 0.7, 0.4].map((k, i) => {
                  const h = Math.max(4, Math.min(22, 4 + waveLevel * 22 * k));
                  return (
                    <span key={i}
                      data-testid={`claire-widget-wave-bar-${i}`}
                      className="w-1 rounded-full transition-all duration-75"
                      style={{ height: `${h}px`, background: "#EF4444" }} />
                  );
                })}
              </div>
            )}
            {/* Iter279 · Voice / mic button — press to record, press
                again to stop. Auto-stops after 30s. Hidden when the
                browser lacks MediaRecorder support. */}
            {supportsMic && (
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing || sending}
                data-testid="claire-widget-mic"
                aria-pressed={recording}
                aria-label={recording ? "Parar gravação" : "Falar com a Claire"}
                title={recording ? "Parar gravação" : "Falar com a Claire (Whisper → Sonnet 5)"}
                className={`w-10 h-10 rounded-full text-white flex items-center justify-center disabled:opacity-50 transition-all relative ${
                  recording
                    ? "bg-[#EF4444] shadow-lg shadow-red-500/40"
                    : "bg-white/8 hover:bg-white/15 border border-white/15"
                }`}
              >
                {transcribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : recording ? (
                  <>
                    <MicOff className="w-4 h-4 relative z-10" />
                    <span className="absolute inset-0 rounded-full bg-red-500/60 animate-ping" />
                  </>
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              type="submit"
              disabled={sending || transcribing || recording || !input.trim()}
              data-testid="claire-widget-send"
              className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white flex items-center justify-center disabled:opacity-50 transition"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </form>

          {/* WhatsApp escalation — always visible at the bottom of the
              panel. Animated pulse highlight when Claire's last reply
              looks uncertain (heuristic on backend → data.uncertain). */}
          {whatsappUrl && (
            <a
              href={`${whatsappUrl}?text=${encodeURIComponent(
                isSales
                  ? "Olá! Vi a Claire na landing do AgenteLead Pro e gostaria de mais informações."
                  : "Olá! Preciso de suporte humano com o AgenteLead Pro.",
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                try {
                  axios.post(`${API}/claire/event`, {
                    type: "whatsapp_escalation",
                    page: pathname,
                    extra: { uncertain: lastUncertain, mode },
                  });
                } catch (_e) { /* fire-and-forget */ }
              }}
              data-testid="claire-widget-whatsapp"
              className={`block text-center text-[12.5px] font-semibold py-2.5 border-t border-white/10 transition-colors ${
                lastUncertain
                  ? "bg-[#25D366]/25 text-[#25D366] animate-pulse"
                  : "bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20"
              }`}
            >
              <Phone className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
              {t("claire.whatsapp")}
            </a>
          )}
        </div>
      )}
    </>
  );
}
